import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Check, RotateCcw, X } from 'lucide-react';
import { Button } from '../../components/Button';
import { useI18n } from '../../i18n/I18nProvider';
import { useModalFocus } from '../../components/useModalFocus';

export function CameraCapture({ onCapture, onClose }: { onCapture: (file: File) => void; onClose: () => void }) {
  const { t } = useI18n();
  const video = useRef<HTMLVideoElement>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [captured, setCaptured] = useState<File | null>(null);
  const [capturedUrl, setCapturedUrl] = useState('');

  const stop = useCallback(() => {
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
  }, []);

  useEffect(() => {
    let active = true;
    if (!navigator.mediaDevices?.getUserMedia) { setError(t('camera.unsupported')); return () => { active = false; stop(); }; }
    void navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false })
      .then((next) => {
        if (!active) { next.getTracks().forEach((track) => track.stop()); return; }
        stream.current = next;
        if (video.current) { video.current.srcObject = next; void video.current.play().then(() => setReady(true)); }
      })
      .catch((cause: unknown) => {
        const name = cause instanceof DOMException ? cause.name : '';
        setError(name === 'NotAllowedError' || name === 'SecurityError' ? t('camera.denied') : name === 'NotFoundError' || name === 'OverconstrainedError' ? t('camera.notFound') : t('camera.unavailable'));
      });
    return () => { active = false; stop(); };
  }, [stop, t]);

  useEffect(() => () => { if (capturedUrl) URL.revokeObjectURL(capturedUrl); }, [capturedUrl]);

  const close = () => { stop(); onClose(); };
  useModalFocus(true, dialog, close);
  const choose = (file: File) => { stop(); onCapture(file); };
  const takePhoto = () => {
    const source = video.current;
    if (!source?.videoWidth || !source.videoHeight) return;
    const canvas = document.createElement('canvas');
    canvas.width = source.videoWidth; canvas.height = source.videoHeight;
    canvas.getContext('2d')?.drawImage(source, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) { setError(t('camera.captureFailed')); return; }
      const file = new File([blob], `walld-camera-${Date.now()}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
      setCaptured(file); setCapturedUrl(URL.createObjectURL(file)); source.pause();
    }, 'image/jpeg', .92);
  };
  const retake = () => { setCaptured(null); setCapturedUrl(''); setError(''); void video.current?.play(); };

  return <div ref={dialog} className={`camera-modal${error && !ready && !capturedUrl ? ' is-unavailable' : ''}`} role="dialog" aria-modal="true" aria-label={t('camera.preview')}>
    <div className="camera-modal__top"><strong>{t('camera.title')}</strong><button data-modal-close className="icon-button" onClick={close} aria-label={t('camera.close')}><X /></button></div>
    <div className="camera-preview">
      <video ref={video} playsInline muted />
      {capturedUrl && <img src={capturedUrl} alt={t('camera.frozenPreview')} />}
      {!ready && !capturedUrl && <div className="camera-preview__state"><Camera />{!error && t('camera.preparing')}</div>}
    </div>
    {error && <p className="inline-error" role="alert">{error}</p>}
    <div className="camera-modal__actions">
      {captured ? <><Button block variant="secondary" icon={<RotateCcw />} onClick={retake}>{t('camera.retake')}</Button><Button block icon={<Check />} onClick={() => choose(captured)}>{t('camera.usePhoto')}</Button></>
        : <Button block icon={<Camera />} onClick={takePhoto} disabled={!ready}>{t('camera.takePhoto')}</Button>}
    </div>
  </div>;
}
